/// <amd-module name="VSSUI/Components/Tabs/Tab" />
import * as React from "react";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { ITabProps } from "VSSUI/Tabs.Types";
import { ITooltipProps, Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import { TabBadge } from "./TabBadge";

/**
 * Presentational component that represents a single tab.
 */
export class Tab extends React.Component<ITabProps> {
    public render(): JSX.Element {
        const { ariaLabel, index, setSize, iconProps, id, isSelected, renderBadge, url } = this.props;
        const TagName = url ? "a" : "div";

        return (
            <Observer name={this.props.name} badgeCount={this.props.badgeCount}>
                {(props: { name?: string; badgeCount?: number }) => {
                    const name = props.name;
                    return (
                        <FocusGroupContext.Consumer>
                            {focusGroupContext => (
                                <FocusZoneContext.Consumer>
                                    {zoneContext => {
                                        const badge = renderBadge ? renderBadge() : this.renderBadge(props.badgeCount);
                                        const icon = iconProps !== undefined && Icon({ className: "bolt-tab-icon", ...iconProps });

                                        const text = name && (
                                            <span className="bolt-tab-text" data-content={name}>
                                                {name}
                                            </span>
                                        );

                                        const tooltipProps: ITooltipProps =
                                            icon && !name && ariaLabel
                                                ? {
                                                      text: ariaLabel,
                                                      overflowOnly: false
                                                  }
                                                : {
                                                      text: name,
                                                      overflowDetected,
                                                      overflowOnly: true
                                                  };

                                        return (
                                            <Tooltip {...tooltipProps}>
                                                <TagName
                                                    aria-label={ariaLabel}
                                                    aria-posinset={index !== undefined ? index + 1 : undefined}
                                                    aria-selected={isSelected}
                                                    aria-setsize={setSize}
                                                    className={css(
                                                        this.props.className,
                                                        "bolt-tab focus-treatment flex-noshrink",
                                                        isSelected && "selected"
                                                    )}
                                                    data-focuszone={zoneContext.focuszoneId}
                                                    href={url}
                                                    id={getSafeId("tab-" + id)}
                                                    key={id}
                                                    onClick={this.onClick}
                                                    onKeyDown={this.onKeyDown}
                                                    role="tab"
                                                    tabIndex={focusGroupContext.focusedElementId === "tab-" + id ? 0 : -1}
                                                >
                                                    <span className="bolt-tab-inner-container">
                                                        {icon}
                                                        {text}
                                                        {badge}
                                                    </span>
                                                </TagName>
                                            </Tooltip>
                                        );
                                    }}
                                </FocusZoneContext.Consumer>
                            )}
                        </FocusGroupContext.Consumer>
                    );
                }}
            </Observer>
        );
    }

    private renderBadge(badgeCount?: number): JSX.Element | null {
        const badgeDisplayValue = badgeCount !== undefined ? getFriendlyDisplayValue(badgeCount) : undefined;
        let badgeTooltip: string | undefined = undefined;
        if (badgeCount && badgeDisplayValue !== badgeCount.toString()) {
            badgeTooltip = badgeCount.toString();
        }

        let badge = null;
        if (badgeDisplayValue) {
            const tooltipProps: ITooltipProps | undefined = badgeTooltip ? { text: badgeTooltip } : undefined;
            badge = <TabBadge tooltipProps={tooltipProps}>{badgeDisplayValue}</TabBadge>;
        }
        return badge;
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        const { url } = this.props;

        let updatePivot = true;

        // If ctrl-click is pressed, and there is a URL specified for this item, then
        // don't handle the click here, allowing the browser to perform a navigation
        // (i.e. open in a new tab/window)
        if (event.ctrlKey) {
            if (url) {
                updatePivot = false;
            }
        }

        if (updatePivot) {
            event.preventDefault();
            this.updateSelectedItem(event);
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.space || event.which === KeyCode.enter) {
                event.preventDefault();
                this.updateSelectedItem(event);
            }
        }
    };

    /**
     * Updates the state with the new selected pivot.
     */
    private updateSelectedItem(ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | null) {
        const { onClick } = this.props;

        if (ev && this.props.onBeforeTabChange && !this.props.onBeforeTabChange(ev, this.props.id, this.props.url)) {
            ev.preventDefault();
            return;
        }

        // Update notifiers
        if (onClick) {
            onClick(this.props.id);
        }
    }
}

function overflowDetected(anchorElement: HTMLElement): boolean {
    const overflowElement = anchorElement.querySelector<HTMLElement>(".bolt-tab-text");
    if (overflowElement) {
        return overflowElement && overflowElement.scrollWidth > Math.ceil(overflowElement.offsetWidth);
    }
    return false;
}
